import React, { FC } from 'react';
import ReactDOM from 'react-dom';
import { Box, Svg, Typography } from '@altermaind/web/ui';
import { TooltipBox } from './styles';
import { TooltipProps } from './types';
import { useTooltip } from './useTooltip';

/**
 * Tooltip component that displays an informative message when the user hovers over the associated icon.
 *
 * This component renders its tooltip content inside a React Portal attached to `document.body` to prevent
 * overflow or stacking context issues. It automatically calculates the best position for the tooltip based on
 * the available space. By default, the tooltip is positioned vertically (above or below the target element),
 * but it can also be positioned horizontally (to the left or right) by enabling the `horizontalTooltips` prop.
 *
 * @param iconProps Optional properties to customize the Svg icon.
 * @param iconName The name of the icon to display. Defaults to 'info'.
 * @param textProps Optional properties to customize the Typography component used for the tooltip text.
 * @param tooltipGap Specifies the spacing between the tooltip and the target element. The value should be a key from the Sizes object.
 * @param children The text content to display inside the tooltip.
 * @param horizontalTooltips When true, the tooltip positions itself horizontally relative to the target element.
 * @param defaultHorizontalPosition When horizontalTooltips is true, defines the default horizontal alignment ('left' or 'right').
 * @param defaultVerticalPosition Defines the default vertical alignment ('top' or 'bottom').
 *
 * @example
 * // Vertical tooltip (default):
 * <Tooltip iconProps={{ size: 'sm' }} textProps={{ variant: 'p2' }} tooltipGap="xxs">
 *   This is a tooltip!
 * </Tooltip>
 *
 * @example
 * // Horizontal tooltip:
 * <Tooltip
 *   iconProps={{ size: 'sm' }}
 *   textProps={{ variant: 'p2' }}
 *   horizontalTooltips
 *   defaultHorizontalPosition="left"
 * >
 *   This is a horizontal tooltip!
 * </Tooltip>
 */
export const Tooltip: FC<TooltipProps> = ({
  iconProps = {},
  iconName = 'info',
  textProps = {},
  containerProps = {},
  tooltipGap = 'xxxs',
  children,
  defaultVerticalPosition = 'top',
  horizontalTooltips = false,
  defaultHorizontalPosition = 'left',
  customShow = false,
}) => {
  const {
    show,
    position,
    coordinates,
    containerRef,
    iconRef,
    tooltipRef,
    handleMouseEnter,
    handleMouseLeave,
  } = useTooltip({
    defaultVerticalPosition,
    defaultHorizontalPosition,
    horizontalTooltips,
    tooltipGap,
  });

  const TooltipContent =
    show || customShow ? (
      <TooltipBox
        zIndex={99999}
        ref={tooltipRef}
        tooltipPosition={position}
        paddingY='xs'
        paddingX='s'
        borderRadius={10}
        maxWidth={250}
        width='max-content'
        whiteSpace='break-spaces'
        position='absolute'
        // TODO: Add dynamic color if light mode will be enabled
        backgroundColor='white'
        top={coordinates.top}
        left={coordinates.left}
        // Inline styles for positioning: if horizontalTooltips is true, position horizontally,
        // otherwise position vertically
        style={{
          ...(horizontalTooltips
            ? {
                transform: 'translateY(-50%)',
              }
            : {
                transform: 'translateX(-50%)',
              }),
        }}
      >
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <Typography variant='p2/light' textAlign='center' {...textProps} color='secondary'>
          {children}
        </Typography>
      </TooltipBox>
    ) : null;

  return (
    <Box
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...containerProps}
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      display='inline-block'
      position='relative'
      data-tooltip
    >
      <Box ref={iconRef} display='flex'>
        {/* eslint-disable-next-line local-rules/no-svg-spread, react/jsx-props-no-spreading */}
        <Svg name={iconName} size='xs' fill='primary/text' {...iconProps} />
      </Box>

      {ReactDOM.createPortal(TooltipContent, document.body)}
    </Box>
  );
};
